import { Injectable, signal } from '@angular/core';
import { ProductApiService } from './product-api.service';
import { Product } from 'src/app/models/core/product.model';
import { forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  constructor(
    private api: ProductApiService,
  ) { }

  // === Sold products ===
  readonly soldProductsEcommerce = signal<Product[]>([]);
  readonly soldProductsLink = signal<Product[]>([]);
  readonly ecommerceCount = signal(0);
  readonly linkCount = signal(0);
  readonly ecommerceCurrentPage = signal(1);
  readonly linkCurrentPage = signal(1);
  readonly ecommerceLastPage = signal(1);
  readonly linkLastPage = signal(1);
  readonly filterByName = signal('');


  // === Top 4 sold products ===
  readonly topSellerProducts = signal<Product[]>([]);
  readonly eventName = signal<string | null>(null);
  readonly ecommerceFilter = signal<number | null>(null);

  // === Microsite products ===
  readonly restrictedProducts = signal<Product[]>([]);
  readonly promotionalProducts = signal<Product[]>([]);
  readonly loadingMicrositeProducts = signal(false);

  // === Loading states ===
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly fullLoading = signal(false);
  readonly partialLoading = signal(false);

  // === Cache system ===
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private currentEventId: number | null = null;
  private readonly lastFetchTimes = {
    topSellers: 0,
    soldProducts: 0
  };

  // === Cache validation methods ===
  private isTopSellersCacheValid(eventId: number): boolean {
    if (this.currentEventId !== eventId) {
      return false;
    }
    
    const now = Date.now();
    const lastFetch = this.lastFetchTimes.topSellers;
    const hasData = this.topSellerProducts().length > 0;
    return hasData && (now - lastFetch) < this.CACHE_DURATION;
  }

  private isSoldProductsCacheValid(eventId: number): boolean {
    if (this.currentEventId !== eventId) {
      return false;
    }
    
    const now = Date.now();
    const lastFetch = this.lastFetchTimes.soldProducts;
    const hasData = this.soldProductsEcommerce().length > 0 || this.soldProductsLink().length > 0;
    return hasData && (now - lastFetch) < this.CACHE_DURATION;
  }

  // === Event change detection ===
  private handleEventChange(newEventId: number) {
    if (this.currentEventId !== null && this.currentEventId !== newEventId) {
      this.clearEventData();
    }
    this.currentEventId = newEventId;
  }

  private clearEventData() {
    this.topSellerProducts.set([]);
    this.soldProductsEcommerce.set([]);
    this.soldProductsLink.set([]);
    this.eventName.set(null);
    this.filterByName.set('');
    this.ecommerceFilter.set(null);
    this.ecommerceCount.set(0);
    this.linkCount.set(0);
    this.ecommerceCurrentPage.set(1);
    this.linkCurrentPage.set(1);
    this.ecommerceLastPage.set(1);
    this.linkLastPage.set(1);
    this.loading.set(false);
    this.loadingMore.set(false);
    this.fullLoading.set(false);
    this.partialLoading.set(false);
    this.restrictedProducts.set([]);
    this.promotionalProducts.set([]);
    this.loadingMicrositeProducts.set(false);
    this.lastFetchTimes.topSellers = 0;
    this.lastFetchTimes.soldProducts = 0;
  }

  fetchTopSellerProducts(eventId: number, type: 'full' | 'partial' = 'full') {
    this.handleEventChange(eventId);
    
    if (this.isTopSellersCacheValid(eventId) && type === 'partial') {
      return;
    }

    const hasData = this.topSellerProducts().length > 0;
    const shouldShowFullLoading = type === 'full' && !hasData;
    
    this.loading.set(shouldShowFullLoading);
    this.partialLoading.set(type === 'partial' && hasData);

    this.api.getTopSellerProducts(eventId).subscribe({
      next: res => {
        this.topSellerProducts.set(res.data);
        this.lastFetchTimes.topSellers = Date.now();
        this.loading.set(false);
        this.partialLoading.set(false);
      },
      error: err => {
        console.error('Error al cargar productos top-seller', err);
        this.loading.set(false);
        this.partialLoading.set(false);
      }
    });
  }


  loadInitialSoldProductsByType(eventId: number, q?: string, type: 'full' | 'partial' = 'full') {
    this.handleEventChange(eventId);
    
    const hasSearchTerm = q && q.trim().length > 0;
    
    if (this.isSoldProductsCacheValid(eventId) && type === 'partial' && !hasSearchTerm) {
      return; 
    }

    // Determine loading state based on whether we have data
    const hasData = this.soldProductsEcommerce().length > 0 || this.soldProductsLink().length > 0;
    const shouldShowFullLoading = type === 'full' && !hasData;
    
    this.fullLoading.set(shouldShowFullLoading);
    this.partialLoading.set(type === 'partial' && hasData);

    const ecommerce$ = this.api.getAllMyTopSellerProducts(eventId, 1, 10, 1, q);
    const link$ = this.api.getAllMyTopSellerProducts(eventId, 1, 10, 0, q);

    forkJoin([ecommerce$, link$]).subscribe({
      next: ([ecommerceRes, linkRes]) => {
        this.updateSoldProductsList(1, ecommerceRes.data, ecommerceRes.pagination, false);
        this.updateSoldProductsList(0, linkRes.data, linkRes.pagination, false);
        this.eventName.set(ecommerceRes.event_name ?? linkRes.event_name ?? null);
        this.lastFetchTimes.soldProducts = Date.now();
        this.fullLoading.set(false);
        this.partialLoading.set(false);
      },
      error: err => {
        console.error('Error al cargar productos por tipo', err);
        this.fullLoading.set(false);
        this.partialLoading.set(false);
      }
    });
  }

  loadMoreSoldProductsByType(
    eventId: number,
    page = 1,
    perPage = 6,
    ecommerce: number,
    q?: string
  ) {
    this.loadingMore.set(true);

    this.api.getAllMyTopSellerProducts(eventId, page, perPage, ecommerce, q).subscribe({
      next: res => {
        this.updateSoldProductsList(ecommerce, res.data, res.pagination, true);
        this.loadingMore.set(false);
      },
      error: err => {
        console.error('Error al cargar productos paginados por tipo', err);
        this.loadingMore.set(false);
      }
    });
  }


  getLastPageByType(ecommerce: number): number {
    return ecommerce === 1 ? this.ecommerceLastPage() : this.linkLastPage();
  }

  private updateSoldProductsList(
    ecommerce: number,
    newProducts: Product[],
    pagination: { current_page: number; last_page: number; total?: number },
    append = true
  ) {
    const currentList = ecommerce === 1
      ? this.soldProductsEcommerce()
      : this.soldProductsLink();

    const currentIds = new Set(currentList.map(p => p.id));
    const merged = append
      ? [...currentList, ...newProducts.filter(p => !currentIds.has(p.id))]
      : newProducts;

    if (ecommerce === 1) {
      this.soldProductsEcommerce.set(merged);
      this.ecommerceCurrentPage.set(pagination.current_page);
      this.ecommerceLastPage.set(pagination.last_page);
      if (pagination.total !== undefined) this.ecommerceCount.set(pagination.total);
    } else {
      this.soldProductsLink.set(merged);
      this.linkCurrentPage.set(pagination.current_page);
      this.linkLastPage.set(pagination.last_page);
      if (pagination.total !== undefined) this.linkCount.set(pagination.total);
    }
  }

  setSearchTerm(term: string) {
    this.filterByName.set(term);
  }

  forceRefreshTopSellers(eventId: number) {
    this.lastFetchTimes.topSellers = 0; 
    this.fetchTopSellerProducts(eventId, 'full');
  }

  forceRefreshSoldProducts(eventId: number, q?: string) {
    this.lastFetchTimes.soldProducts = 0; 
    this.loadInitialSoldProductsByType(eventId, q, 'full');
  }

  // === Microsite products methods ===
  loadMicrositeProducts(eventId: number) {
    this.loadingMicrositeProducts.set(true);

    const restricted$ = this.api.getRestrictedProducts(eventId);
    const promotional$ = this.api.getPromotionalProducts(eventId);

    forkJoin([restricted$, promotional$]).subscribe({
      next: ([restrictedRes, promotionalRes]) => {
        this.restrictedProducts.set(restrictedRes.data);
        this.promotionalProducts.set(promotionalRes.data);
        this.loadingMicrositeProducts.set(false);
      },
      error: err => {
        console.error('Error al cargar productos del microsite', err);
        this.loadingMicrositeProducts.set(false);
      }
    });
  }

  // === Reset state methods ===
  resetAllState(): void {
    this.topSellerProducts.set([]);
    this.soldProductsEcommerce.set([]);
    this.soldProductsLink.set([]);
    this.eventName.set(null);
    this.filterByName.set('');
    this.ecommerceFilter.set(null);
    this.ecommerceCount.set(0);
    this.linkCount.set(0);
    this.ecommerceCurrentPage.set(1);
    this.linkCurrentPage.set(1);
    this.ecommerceLastPage.set(1);
    this.linkLastPage.set(1);
    this.loading.set(false);
    this.loadingMore.set(false);
    this.fullLoading.set(false);
    this.partialLoading.set(false);
    this.restrictedProducts.set([]);
    this.promotionalProducts.set([]);
    this.loadingMicrositeProducts.set(false);
    this.lastFetchTimes.topSellers = 0;
    this.lastFetchTimes.soldProducts = 0;
  }

}
